import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  selectedDrone: null,
  control: {
    powerStatus: false,
    armingStatus: false,
    missionStatus: false,
    serialNum: "",
    // gps_status(1~8) 정수. null = 미수신. 5,6 만 비행 가능.
    gpsStatus: null,
  },
  droneData: null,
  connected: false,
  // 서버 하트비트 기준 드론 연결 여부 (REST /info 폴링에서 갱신).
  // null = 아직 모름, true/false = 서버 판정. FlightNote 가 오프라인 표시에 사용.
  infoConnected: null,
  // mission_complete 수신 후 착륙(disarm)까지 true — 복귀 구간 동안 미션 버튼 잠금용.
  // control 객체 밖에 두는 이유: setControl(data.data)처럼 서버 응답으로 control을
  // 통째 교체하는 호출에서 플래그가 소실되지 않게 하기 위함.
  missionReturning: false,
  // 드론이 미션 경로를 보관 중인지(텔레메트리 upload_status) + 그때 보고한 route_id.
  // 업로드 완료 판정과 Start 활성 조건. arm 은 이제 Start(start_mission) 때 드론이 하므로
  // ARMED 가 아니라 이 값이 "업로드 됐다"의 근거다. control 밖에 두는 이유는 missionReturning 과 같다.
  // pendingAt: 업로드를 접수한 시각. 확인을 기다리는 중이면 값이 있다. 서버가 내려 주므로 누르지 않은 PC 도 대기 표시를 낸다.
  upload: { status: false, routeId: null, pendingAt: null },
  // 드론이 보고한 미션 수행 단계(mission_progress 문자열). null = 미수신. 표시 전용.
  missionProgress: null,
  // 마지막으로 완주(complete)한 미션의 결과 { done, total }. 착륙 후 진행 링이 0/0 으로 지워지지 않고
  // "N/N · 100%" 를 유지하게 한다. 실기체는 착륙 10초 뒤 progress 를 idle 로 되돌리므로 progress 로는
  // 복원할 수 없어 여기 래치로 둔다. 사용자가 다시 Upload 를 누르거나 Start 하면 지운다.
  lastMissionResult: null,
  // 착륙 뒤 완주 결과를 한 번 내린 다음, 같은 비행의 늦은 complete 보고나 새로고침 복원이
  // 그 결과를 되살리지 못하게 막는다. 드론은 착륙 후 10초쯤 complete 를 계속 보고한다.
  // 새 미션이 시작되거나 새로 업로드하면 풀린다.
  missionResultDismissed: false,
};

const droneSlice = createSlice({
  name: "drone",
  initialState,
  reducers: {
    setSelectedDrone: (state, action) => {
      state.selectedDrone = action.payload;
    },
    setControl: (state, action) => {
      state.control = action.payload;
    },
    updateDroneData(state, action) {
      state.droneData = {
        ...(state.droneData ?? {}),
        ...(action.payload ?? {}),
      };
    },
    resetMissionProgress(state, action) {
      const total = Number(action.payload ?? 0);
      // 새 미션 시작 — 직전 완주 결과는 더 이상 보여줄 이유가 없다.
      state.lastMissionResult = null;
      // 이번 비행의 결과는 다시 래치될 수 있어야 하므로 잠금을 푼다.
      state.missionResultDismissed = false;
      state.droneData = {
        ...(state.droneData ?? {}),
        waypoint_done: 0,
        waypoint_total: Number.isFinite(total)
          ? Math.max(0, Math.round(total))
          : 0,
        waypoint_percent: 0,
        eta: null,
        flight_time_sec: null,
        time_left_sec: null,
        takeoff_at: null,
      };
    },
    setConnected(state, action) {
      state.connected = action.payload;
    },
    setInfoConnected(state, action) {
      state.infoConnected =
        typeof action.payload === "boolean" ? action.payload : null;
    },
    setMissionReturning(state, action) {
      state.missionReturning = Boolean(action.payload);
    },
    setMissionProgress(state, action) {
      const p =
        typeof action.payload === "string" && action.payload
          ? action.payload
          : null;
      if (state.missionProgress !== p) {
        state.missionProgress = p;
      }
    },
    // complete 프레임(WS) 또는 새로고침 복원(REST progress=complete)에서 호출.
    // total 을 안 주면 진행 중이던 미션의 waypoint_total 을 쓴다. complete = 전 구역 완료이므로 done=total.
    latchMissionResult(state, action) {
      // 착륙 뒤 이미 한 번 내린 결과는 되살리지 않는다.
      if (state.missionResultDismissed) return;
      const raw = action.payload?.total ?? state.droneData?.waypoint_total;
      const total = Number(raw);
      if (!Number.isFinite(total) || total <= 0) return;
      const rounded = Math.round(total);
      if (
        state.lastMissionResult?.total === rounded &&
        state.lastMissionResult?.done === rounded
      ) {
        return;
      }
      state.lastMissionResult = { done: rounded, total: rounded };
    },
    // 사용자가 새 업로드를 시작할 때(Upload 클릭), 그리고 착륙 뒤 결과를 잠깐 보여 준 다음 호출.
    // 착륙 뒤 호출이면 dismiss=true 로 잠가, 늦게 오는 complete 나 새로고침 복원이 되살리지 못하게 한다.
    clearMissionResult(state, action) {
      state.lastMissionResult = null;
      state.missionResultDismissed = action?.payload?.dismiss === true;
    },
    setUploadStatus(state, action) {
      const p = action.payload ?? {};
      const status = Boolean(p.status);
      const routeId = p.routeId ?? null;
      // pendingAt 을 안 넘기면 대기가 끝난 것으로 본다. 확인/취소/시작 모두 대기를 끝내는 사건이라 이게 맞다.
      const pendingAt = p.pendingAt ?? null;
      // 같은 값이면 건드리지 않아 구독 컴포넌트가 불필요하게 다시 그리지 않게 한다.
      if (
        state.upload.status !== status ||
        state.upload.routeId !== routeId ||
        state.upload.pendingAt !== pendingAt
      ) {
        state.upload = { status, routeId, pendingAt };
      }
    },
    clearDroneData(state) {
      state.droneData = null;
      state.connected = false;
      state.infoConnected = null;
      // 소켓 해제(페이지 이탈/드론 전환) 시 잠금이 세션에 남지 않게 함께 리셋
      state.missionReturning = false;
      state.upload = { status: false, routeId: null, pendingAt: null };
      state.missionProgress = null;
      state.lastMissionResult = null;
      state.missionResultDismissed = false;
    },
  },
});

export const {
  setSelectedDrone,
  setControl,
  updateDroneData,
  resetMissionProgress,
  setConnected,
  setInfoConnected,
  setMissionProgress,
  latchMissionResult,
  clearMissionResult,
  setMissionReturning,
  setUploadStatus,
  clearDroneData,
} = droneSlice.actions;
export default droneSlice.reducer;
